import { HttpClient, HttpHeaders, HttpResponse, HttpResponseBase } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of as _observableOf, throwError as _observableThrow } from 'rxjs';
import { catchError as _observableCatch, mergeMap as _observableMergeMap } from 'rxjs/operators';
import { CommonFunctions } from '../common-functions';
import { BooleanApiResponse } from '../models/boolean-api-response.model';
import { CreateOrUpdateExtraPayApplicationDto } from '../models/create-or-update-extra-pay-application-dto.model';
import { ExtraPayApplicationDtoApiResponse } from '../models/extra-pay-application-dto-api-response.model';
import { ExtraPayApplicationDtoListApiResponse } from '../models/extra-pay-application-dto-list-api-response.model';
import { TransactionApplicationDto } from '../models/transaction-application-dto.model';
import { API_BASE_URL } from '../service-proxies';

@Injectable()
export class ExtraPayApplicationServiceProxy {
    private http: HttpClient;
    private baseUrl: string;
    protected jsonParseReviver: ((key: string, value: any) => any) | undefined = undefined;

    constructor(private _commonFunctions: CommonFunctions, @Inject(HttpClient) http: HttpClient, @Optional() @Inject(API_BASE_URL) baseUrl?: string) {
        this.http = http;
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
    }

    /**
     * @param body (optional) 
     * @return Success
     */
    getAllExtraPayApplications(body: TransactionApplicationDto | undefined): Observable<ExtraPayApplicationDtoListApiResponse> {
        let url_ = this.baseUrl + "/api/ExtraPayApplication/GetAllExtraPayApplications";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json-patch+json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetAllExtraPayApplications(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetAllExtraPayApplications(<any>response_);
                } catch (e) {
                    return <Observable<ExtraPayApplicationDtoListApiResponse>><any>_observableThrow(e);
                }
            } else
                return <Observable<ExtraPayApplicationDtoListApiResponse>><any>_observableThrow(response_);
        }));
    }

    protected processGetAllExtraPayApplications(response: HttpResponseBase): Observable<ExtraPayApplicationDtoListApiResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = ExtraPayApplicationDtoListApiResponse.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<ExtraPayApplicationDtoListApiResponse>(<any>null);
    }

    /**
     * @param id (optional) 
     * @return Success
     */
    getExtraPayApplication(id: number | undefined): Observable<ExtraPayApplicationDtoApiResponse> {
        let url_ = this.baseUrl + "/api/ExtraPayApplication/GetExtraPayApplication?";
        if (id === null)
            throw new Error("The parameter 'id' cannot be null.");
        else if (id !== undefined)
            url_ += "id=" + encodeURIComponent("" + id) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "text/plain"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetExtraPayApplication(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetExtraPayApplication(<any>response_);
                } catch (e) {
                    return <Observable<ExtraPayApplicationDtoApiResponse>><any>_observableThrow(e);
                }
            } else
                return <Observable<ExtraPayApplicationDtoApiResponse>><any>_observableThrow(response_);
        }));
    }

    protected processGetExtraPayApplication(response: HttpResponseBase): Observable<ExtraPayApplicationDtoApiResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = ExtraPayApplicationDtoApiResponse.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<ExtraPayApplicationDtoApiResponse>(<any>null);
    }

    /**
     * @param currentMonth (optional) 
     * @return Success
     */
    getLastExtraPayApplication(currentMonth: string | undefined): Observable<ExtraPayApplicationDtoListApiResponse> {
        let url_ = this.baseUrl + "/api/ExtraPayApplication/GetLastExtraPayApplication?";
        if (currentMonth === null)
            throw new Error("The parameter 'currentMonth' cannot be null.");
        else if (currentMonth !== undefined)
            url_ += "currentMonth=" + encodeURIComponent("" + currentMonth) + "&";
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "text/plain"
            })
        };

        return this.http.request("get", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processGetLastExtraPayApplication(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processGetLastExtraPayApplication(<any>response_);
                } catch (e) {
                    return <Observable<ExtraPayApplicationDtoListApiResponse>><any>_observableThrow(e);
                }
            } else
                return <Observable<ExtraPayApplicationDtoListApiResponse>><any>_observableThrow(response_);
        }));
    }

    protected processGetLastExtraPayApplication(response: HttpResponseBase): Observable<ExtraPayApplicationDtoListApiResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = ExtraPayApplicationDtoListApiResponse.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<ExtraPayApplicationDtoListApiResponse>(<any>null);
    }

    /**
     * @param body (optional) 
     * @return Success
     */
    createOrUpdateExtraPayApplication(body: CreateOrUpdateExtraPayApplicationDto[] | undefined): Observable<ExtraPayApplicationDtoListApiResponse> {
        let url_ = this.baseUrl + "/api/ExtraPayApplication/CreateOrUpdateExtraPayApplication";
        url_ = url_.replace(/[?&]$/, "");

        const content_ = JSON.stringify(body);

        let options_ : any = {
            body: content_,
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Content-Type": "application/json-patch+json",
                "Accept": "text/plain"
            })
        };

        return this.http.request("post", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processCreateOrUpdateExtraPayApplication(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processCreateOrUpdateExtraPayApplication(<any>response_);
                } catch (e) {
                    return <Observable<ExtraPayApplicationDtoListApiResponse>><any>_observableThrow(e);
                }
            } else
                return <Observable<ExtraPayApplicationDtoListApiResponse>><any>_observableThrow(response_);
        }));
    }

    protected processCreateOrUpdateExtraPayApplication(response: HttpResponseBase): Observable<ExtraPayApplicationDtoListApiResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = ExtraPayApplicationDtoListApiResponse.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<ExtraPayApplicationDtoListApiResponse>(<any>null);
    }

    /**
     * @return Success
     */
    deleteExtraPayApplication(id: number): Observable<BooleanApiResponse> {
        let url_ = this.baseUrl + "/api/ExtraPayApplication/DeleteExtraPayApplication/{id}";
        if (id === undefined || id === null)
            throw new Error("The parameter 'id' must be defined.");
        url_ = url_.replace("{id}", encodeURIComponent("" + id));
        url_ = url_.replace(/[?&]$/, "");

        let options_ : any = {
            observe: "response",
            responseType: "blob",
            headers: new HttpHeaders({
                "Accept": "text/plain"
            })
        };

        return this.http.request("delete", url_, options_).pipe(_observableMergeMap((response_ : any) => {
            return this.processDeleteExtraPayApplication(response_);
        })).pipe(_observableCatch((response_: any) => {
            if (response_ instanceof HttpResponseBase) {
                try {
                    return this.processDeleteExtraPayApplication(<any>response_);
                } catch (e) {
                    return <Observable<BooleanApiResponse>><any>_observableThrow(e);
                }
            } else
                return <Observable<BooleanApiResponse>><any>_observableThrow(response_);
        }));
    }

    protected processDeleteExtraPayApplication(response: HttpResponseBase): Observable<BooleanApiResponse> {
        const status = response.status;
        const responseBlob =
            response instanceof HttpResponse ? response.body :
            (<any>response).error instanceof Blob ? (<any>response).error : undefined;

        let _headers: any = {}; if (response.headers) { for (let key of response.headers.keys()) { _headers[key] = response.headers.get(key); }}
        if (status === 200) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            let result200: any = null;
            let resultData200 = _responseText === "" ? null : JSON.parse(_responseText, this.jsonParseReviver);
            result200 = BooleanApiResponse.fromJS(resultData200);
            return _observableOf(result200);
            }));
        } else if (status !== 200 && status !== 204) {
            return this._commonFunctions.blobToText(responseBlob).pipe(_observableMergeMap(_responseText => {
            return this._commonFunctions.throwException("An unexpected server error occurred.", status, _responseText, _headers);
            }));
        }
        return _observableOf<BooleanApiResponse>(<any>null);
    }
}
